import React, { useState } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import { apiGet, apiPost } from '../../utils/api';
import { utf8ToBase64 } from '../../utils/utils';
import SubmitButton from '../SubmitButton';
import { useCurrentOrganization, useOrganizationContext } from 'src/contexts/OrganizationContext';
import type { ViewableItem, EmailContext } from './ItemListModal';

interface EmailModalProps {
  context: EmailContext;
  item: ViewableItem;
  onClose: () => void;
}

const EmailModal = ({ context, item, onClose }: EmailModalProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ formData, setFormData ] = useState({
    to_email: '',
    to_name: '',
    subject: `Medical Document - ${context.type} (${context.date})`,
    message: `Please find attached the medical document from your ${context.type} visit on ${context.date}.`
  });
  const [ isLoading, setIsLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ success, setSuccess ] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    setError(null);

    try {
      const binaryMatch = item.binaryUrl.match(/Binary\/([^/]+)/);
      if (!binaryMatch) throw new Error('Invalid binary URL format');

      const binaryId = binaryMatch[1];
      const binaryResponse = await apiGet(`/api/${organization.id}/binary/${patientId || ''}/${binaryId}?contentType=${item.contentType}`);
      const data = await binaryResponse.json();

      const fileContent = utf8ToBase64(data.data);
      const fileName = item.name || 'Document';
      const fileType = item.contentType;

      const htmlContent = `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <h2 style="color: #2563eb;">Medical Document</h2>
              <p>Dear ${formData.to_name || 'Patient'},</p>
              <p>${formData.message}</p>
              <div style="background-color: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0;">
                <h3 style="margin-top: 0; color: #1e40af;">Document Details:</h3>
                <p><strong>Type:</strong> ${context.type}</p>
                <p><strong>Date:</strong> ${context.date}</p>
                <p><strong>Author:</strong> ${context.author}</p>
                <p><strong>Document:</strong> ${item.name}</p>
              </div>
              <p>Please keep this document for your records. If you have any questions, please contact your healthcare provider.</p>
              <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">
              <p style="font-size: 12px; color: #6b7280;">
                This email was sent from PatientDashboard. Please do not reply to this email.
              </p>
            </div>
          </body>
        </html>
      `;

      const textContent = `
Medical Document

Dear ${formData.to_name || 'Patient'},

${formData.message}

Document Details:
- Type: ${context.type}
- Date: ${context.date}
- Author: ${context.author}
- Document: ${item.name}

Please keep this document for your records. If you have any questions, please contact your healthcare provider.

---
This email was sent from PatientDashboard. Please do not reply to this email.
      `;

      const sendEmailResponse = await apiPost(`/api/${organization.id}/send-email/${patientId || ''}`, {
        to_email: formData.to_email,
        subject: formData.subject,
        html_content: htmlContent,
        text_content: textContent,
        file_name: fileName,
        file_binary_id: binaryId,
        file_content: fileContent,
        file_type: fileType,
      });

      if (sendEmailResponse.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const errorData = await sendEmailResponse.json();
        throw new Error(errorData.error || 'Failed to send email');
      }
    } catch (err) {
      setError('Error: ' + (err instanceof Error ? err.message : 'An unknown error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Modal title="Email Sent" onClose={onClose}>
        <div className="p-4 sm:p-6 text-center max-h-[60vh] overflow-y-auto">
          <div className="mx-auto flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-green-100 mb-3 sm:mb-4">
            <EnvelopeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Email Sent Successfully!</h3>
          <p className="text-xs sm:text-sm text-gray-500">
            The medical document has been sent to {formData.to_email}
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Email Document" onClose={onClose}>
      <div className="mb-4 p-3 sm:p-4 bg-brand-50 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Document Information</h3>
        <div className="text-xs sm:text-sm space-y-1">
          <p><strong>Type:</strong> {context.type}</p>
          <p><strong>Date:</strong> {context.date}</p>
          <p><strong>Author:</strong> {context.author}</p>
          <p><strong>Document:</strong> {item.name}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div>
          <label htmlFor="to_email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            Recipient Email *
          </label>
          <input
            type="email"
            id="to_email"
            name="to_email"
            value={formData.to_email}
            onChange={handleInputChange}
            required
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            placeholder="patient@example.com"
          />
        </div>

        <div>
          <label htmlFor="to_name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            Recipient Name
          </label>
          <input
            type="text"
            id="to_name"
            name="to_name"
            value={formData.to_name}
            onChange={handleInputChange}
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            placeholder="Patient Name"
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            Subject *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            required
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
            placeholder="Add a personal message..."
          />
        </div>

        {error && (
          <div className="p-2 sm:p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-xs sm:text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-3 sm:pt-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            Cancel
          </button>
          <SubmitButton
            loading={isLoading}
            loadingText="Sending Email..."
            text="Send Email"
            onClick={(e) => handleSubmit(e)}
          />
        </div>
      </form>
    </Modal>
  );
};

export default EmailModal;
